import GoogleMapReact from 'google-map-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import cdConfig from '~/assets/mapStyle/cd.json';
import localeeConfig from '~/assets/mapStyle/localee.json';
import { company } from '~/company/Company';
import Icon from '~/components/Icon/Icon';
import { ModalType } from '~/components/Modal/interface';
import { REDIRECT_QUERY_NAME } from '~/components/ModalDeliveryAddress/constants';
import {
  deliveryAddressStore,
  mapZoom,
} from '~/components/ModalDeliveryAddress/DeliveryAddressStore';
import type { DeliveryZoneRef } from '~/components/ModalDeliveryAddress/GMap/interfaces';
import { createPolygonForZone } from '~/components/ModalDeliveryAddress/GMap/utils';
import { MODAL_EDIT_ADDRESS_PARAM, useModal } from '~/hooks/useModal';
import { userStore } from '~/stores';
import { GOOGLE_API_KEY } from '~/stores/constants';

import styles from './MapStatic.module.scss';

const mapStyle = company.match({
  citydrinks: cdConfig,
  default: localeeConfig,
});

const MapStatic = observer(() => {
  const [isReady, setIsReady] = useState(false);
  const [searchParams] = useSearchParams();
  const redirectNext = searchParams.get(REDIRECT_QUERY_NAME);

  const [defaultCenter] = useState<GeoCoordinates>(
    () =>
      deliveryAddressStore.deliveryAddress?.coordinates ||
      deliveryAddressStore.mapCenter,
  );

  useEffect(() => {
    //Delay... this is a workaround to fix the issue with the map not loading...
    const timeout = setTimeout(() => {
      setIsReady(true);
    }, 300);

    return () => clearTimeout(timeout);
  }, []);

  const { openModal } = useModal();

  const handleChangeDeliveryAddressLocation = () => {
    openModal(
      ModalType.EditAddressLocation,
      ModalType.EditAddressDetails,
      {
        [MODAL_EDIT_ADDRESS_PARAM]: deliveryAddressStore.editAddressData
          .addressId as string,
        [REDIRECT_QUERY_NAME]: redirectNext || '',
      },
      false,
      true,
    );
  };

  const handleApiLoaded = async ({ map }: { map: google.maps.Map }) => {
    try {
      const coverages = await userStore.getDeliveryZones();
      if (!coverages.length) {
        return;
      }

      const polygonsRef: Record<string, DeliveryZoneRef> = {};

      coverages.forEach((el) => {
        new Map(
          el.zones
            .sort((a, b) => a.priority - b.priority)
            .map((zone) => {
              zone.warehouseId = el.warehouse.id;
              return [zone.name, zone];
            }),
        ).forEach((zone) => {
          const drawPolygons = createPolygonForZone(zone);
          drawPolygons.setMap(map);

          const zoneRef = {
            zone,
            polygon: drawPolygons,
          };

          if (zone.warehouseId) {
            polygonsRef[`${zone.name}-${zone.warehouseId}`] = zoneRef;
          }
        });
      });

      deliveryAddressStore.renderedZonesOnMap = polygonsRef;

      if (userStore.isAuthorized) {
        await userStore.fetchAddresses();
      }
    } catch (error) {
      error && console.error(error);
    }
  };

  const center: GeoCoordinates =
    deliveryAddressStore.deliveryAddress?.coordinates ||
    deliveryAddressStore.mapCenter;

  if (defaultCenter === null) {
    return <></>;
  }

  return (
    <div className={styles.container}>
      <Icon
        type="pin"
        size={company.match({
          citydrinks: 44,
          v3: 44,
          default: 64,
        })}
        className={styles.pin}
      />

      <div className={styles.address}>
        <p className={styles.addressValue}>
          {deliveryAddressStore.deliveryAddress?.address1}
        </p>
        <Icon
          type="edit"
          size={24}
          className={styles.addressEditIcon}
          onClick={handleChangeDeliveryAddressLocation}
        />
      </div>
      {isReady && (
        <GoogleMapReact
          yesIWantToUseGoogleMapApiInternals
          bootstrapURLKeys={{
            key: GOOGLE_API_KEY,
            language: 'en-GB',
            region: 'GB',
          }}
          defaultCenter={defaultCenter}
          defaultZoom={mapZoom}
          zoom={deliveryAddressStore.mapZoom}
          center={center}
          options={{
            clickableIcons: false,
            disableDefaultUI: true,
            gestureHandling: 'greedy',
            styles: mapStyle,
            draggable: false,
          }}
          onGoogleApiLoaded={handleApiLoaded}
          shouldUnregisterMapOnUnmount={true}
          style={{
            pointerEvents: deliveryAddressStore.isChangeDeliveryAreaPopover
              ? 'none'
              : 'auto',
          }}
        />
      )}
    </div>
  );
});

export default MapStatic;
